<template>
  <div class="alibabaorder">
    <!-- 固定頂部導航 -->
    <header class="fixed-header">
      <div class="header-container">
        <a class="brand" href="#">
          <i class="fa fa-shopping-bag"></i>
          <span>開始代購吧</span>
        </a>
        <button
          v-if="addToCartCount > 0"
          type="button"
          class="cart-button"
          @click="goToShoppingCart()"
        >
          <i class="fa fa-shopping-cart"></i>
          <span>購物車</span>
          <span class="cart-badge">{{ addToCartCount }}</span>
        </button>
      </div>
    </header>

    <div class="content-wrapper">
      <!-- 輸入階段 -->
      <div v-if="addToCartCount == 0" class="search-section">
        <div class="search-container">
          <div class="hero-text">
            <h1 class="hero-title">開始您的代購之旅</h1>
            <p class="hero-subtitle">
              輕鬆貼上 1688 商品網址，我們幫您處理繁瑣的採購流程
            </p>
          </div>

          <div class="search-card">
            <div class="search-card-body">
              <label class="input-label">輸入商品連結</label>
              <div class="search-input-group">
                <input
                  type="text"
                  class="search-input"
                  placeholder="例如: https://detail.1688.com/offer/800120939317.html"
                  v-model="searchUrl"
                  @keyup.enter="clickSearchUrl()"
                />
                <button class="search-button" @click="clickSearchUrl()">
                  開始代購
                </button>
              </div>
              <p class="input-hint">
                <i class="fa fa-info-circle"></i>
                支援 desktop/m/share 等各種 1688 連結格式
              </p>

              <!-- 最近搜尋歷史 -->
              <div v-if="searchUrlHistory.length > 0" class="history-section">
                <div class="history-header">
                  <i class="fa fa-history"></i>
                  <span>最近搜尋</span>
                </div>
                <div class="history-list">
                  <div
                    v-for="(item, index) in searchUrlHistory"
                    :key="index"
                    class="history-item"
                    @click="useHistoryUrl(item)"
                    :title="getHistoryItemUrl(item)"
                  >
                    <div class="history-text">
                      {{ getHistoryItemDisplay(item) }}
                    </div>
                    <button
                      class="history-delete"
                      @click.stop="removeSearchUrlHistory(index)"
                      title="刪除"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 網址範例說明 -->
          <div v-if="displayViews == null" class="info-card url-guide">
            <h3 class="info-card-title">阿里巴巴網址範例</h3>
            <div class="url-examples">
              <div class="url-example">
                <div class="url-example-label">
                  <i class="fa fa-desktop"></i>
                  <span>桌面版</span>
                </div>
                <code class="url-code">
                  https://detail.1688.com/offer/800120939317.html
                </code>
              </div>
              <div class="url-example">
                <div class="url-example-label">
                  <i class="fa fa-mobile-alt"></i>
                  <span>手機版</span>
                </div>
                <code class="url-code">
                  https://m.1688.com/offer/800120939317.html
                </code>
              </div>
            </div>
          </div>

          <!-- 免費服務保證 -->
          <div v-if="displayViews == null" class="info-card guarantee-card">
            <div class="guarantee-header">
              <i class="fa fa-check-circle"></i>
              <div>
                <h3>100% 免費，零風險保證</h3>
                <p>安心申請，隨時可取消</p>
              </div>
            </div>
            <ul class="guarantee-list">
              <li>
                <i class="fa fa-check"></i>
                <span>申請委託單<strong>完全免費</strong></span>
              </li>
              <li>
                <i class="fa fa-check"></i>
                <span>收到報價後可<strong>隨時取消委託單</strong></span>
              </li>
              <li class="tip">
                <i class="fa fa-lightbulb"></i>
                <span>
                  <strong>App 小技巧：</strong>在阿里巴巴 App 中，<span
                    class="highlight"
                    >長按商品名稱</span
                  >即可複製網址
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 商品選購階段 -->
      <div v-if="displayViews != null" class="product-section">
        <!-- 缺貨提示 -->
        <div v-if="displayViews.is_out_of_stock" class="alert alert-danger">
          <i class="fa fa-exclamation-triangle"></i>
          <span>此商品目前已售完</span>
        </div>

        <div class="product-layout">
          <!-- 商品主圖 -->
          <div class="product-image-wrapper">
            <div class="product-image-sticky">
              <div class="product-image-card">
                <img
                  class="product-image"
                  :src="displayViews.main_image_url"
                  alt="Product Image"
                  referrerpolicy="no-referrer"
                />
              </div>
            </div>
          </div>

          <!-- 商品資訊與選購 -->
          <div class="product-info-wrapper">
            <div class="product-card">
              <div class="product-card-body">
                <!-- 商品標題與基本資訊 -->
                <h2 class="product-title">{{ displayViews.prod_name }}</h2>
                <div class="product-meta">
                  <span
                    v-if="displayViews.is_custom_order"
                    class="meta-item custom-order-badge"
                  >
                    定制商品
                  </span>
                  <span class="meta-item">
                    起批數量: <strong>{{ buyBaseLine }}</strong>
                  </span>
                  <span
                    v-if="
                      !displayViews.is_custom_order &&
                      displayViews.total_can_book_count !== undefined
                    "
                    class="meta-item"
                  >
                    總庫存:
                    <strong>{{ displayViews.total_can_book_count }}</strong>
                  </span>
                </div>

                <!-- 定制商品單價設定 -->
                <div
                  v-if="displayViews.is_custom_order"
                  class="custom-price-section"
                >
                  <div class="custom-price-header">
                    <i class="fa fa-pencil"></i>
                    <span>定制單價</span>
                  </div>
                  <div class="custom-price-input-wrapper">
                    <span class="custom-price-symbol">¥</span>
                    <input
                      type="number"
                      class="custom-price-input"
                      v-model.number="customPrice"
                      :min="parseFloat(displayViews.cost_price_lowest) || 0.01"
                      :max="
                        parseFloat(displayViews.cost_price_highest) || 99999
                      "
                      step="0.01"
                      placeholder="請輸入單價"
                    />
                    <span class="custom-price-unit">元/件</span>
                  </div>
                </div>

                <!-- 階梯定價 -->
                <div
                  v-if="
                    displayViews.amount_info &&
                    displayViews.amount_info.length > 0
                  "
                  class="tier-pricing"
                >
                  <div class="tier-pricing-header">
                    <i class="fa fa-tags"></i>
                    <span>階梯定價</span>
                  </div>
                  <div class="tier-badges">
                    <span
                      v-for="(tier, index) in sortedAmountInfo"
                      :key="index"
                      class="tier-badge"
                      :class="{
                        'tier-badge-active':
                          orderSummary.totalCount >=
                          parseInt(tier.amount_count),
                      }"
                    >
                      ≥{{ tier.amount_count }}件
                      <strong>¥{{ tier.amount_price }}</strong>
                    </span>
                  </div>
                  <p class="tier-hint">
                    <i class="fa fa-info-circle"></i>
                    購買數量越多，單價越優惠
                  </p>
                </div>

                <div class="divider"></div>

                <!-- 顏色選擇 -->
                <div
                  v-if="
                    displayViews.color_info &&
                    displayViews.color_info.length > 0
                  "
                  class="color-selection"
                >
                  <h3 class="section-title">選擇顏色/款式</h3>
                  <div class="color-grid">
                    <div
                      class="color-item"
                      v-for="(
                        color_info, color_index
                      ) in displayViews.color_info"
                      :key="color_index"
                      :class="{
                        'color-item-disabled': color_info.is_out_of_stock,
                        'color-item-selected': colorSelected === color_index,
                      }"
                      @click="
                        !color_info.is_out_of_stock &&
                          (colorSelected = color_index)
                      "
                      :title="color_info.color_name"
                    >
                      <div class="color-image-wrapper">
                        <img
                          class="color-image"
                          :src="color_info.color_image"
                          alt="Color"
                          loading="lazy"
                          referrerpolicy="no-referrer"
                        />
                        <div
                          v-if="colorSelected === color_index"
                          class="color-checkmark"
                        >
                          <i class="fa fa-check"></i>
                        </div>
                        <span
                          v-if="color_info.is_out_of_stock"
                          class="color-out-of-stock"
                        >
                          缺貨
                        </span>
                      </div>
                      <div class="color-name">
                        {{ color_info.color_name }}
                      </div>
                    </div>
                  </div>
                  <b-form-radio
                    v-model="colorSelected"
                    name="color-radios"
                    value=""
                    class="d-none"
                  ></b-form-radio>
                </div>

                <!-- 規格選擇 -->
                <div class="sku-selection">
                  <h3 class="section-title">選擇規格與數量</h3>

                  <!-- 提示訊息 -->
                  <div
                    v-if="
                      displayViews.color_info &&
                      displayViews.color_info.length > 0 &&
                      colorSelected === null
                    "
                    class="alert alert-warning"
                  >
                    <i class="fa fa-info-circle"></i>
                    <span>請先選擇上方的顏色以查看對應規格</span>
                  </div>
                  <div
                    v-else-if="
                      filteredSkuInfo.length === 0 &&
                      displayViews.sku_info.length > 0
                    "
                    class="alert alert-info"
                  >
                    <span>此顏色組合暫無可選規格</span>
                  </div>

                  <!-- SKU 卡片列表 -->
                  <div v-else class="sku-card-list">
                    <div
                      v-for="sku_item in filteredSkuInfo"
                      :key="sku_item.originalIndex"
                      class="sku-card"
                      :class="{ 'sku-card-disabled': sku_item.is_out_of_stock }"
                    >
                      <div class="sku-card-header">
                        <div class="sku-card-title">
                          <span class="sku-name-text">{{
                            sku_item.displayName
                          }}</span>
                          <span
                            v-if="sku_item.is_out_of_stock"
                            class="sku-out-badge"
                            >缺貨</span
                          >
                        </div>
                        <div class="sku-price-display">
                          <template v-if="displayViews.is_custom_order">
                            ¥{{ customPrice || '--' }}
                          </template>
                          <template v-else>
                            ¥{{ currentTierPrice || sku_item.sku_price }}
                          </template>
                        </div>
                      </div>
                      <div class="sku-card-body">
                        <div
                          v-if="!displayViews.is_custom_order"
                          class="sku-stock-info"
                        >
                          <span class="stock-label">庫存</span>
                          <span class="stock-value">{{
                            sku_item.sku_count || '充足'
                          }}</span>
                        </div>
                        <div class="sku-quantity-wrapper">
                          <button
                            class="qty-btn qty-btn-minus"
                            type="button"
                            @click="lessAmounts(sku_item.originalIndex)"
                            :disabled="sku_item.is_out_of_stock"
                          >
                            <span class="qty-icon">−</span>
                          </button>
                          <input
                            type="number"
                            class="qty-input"
                            v-model="skuInfo[sku_item.originalIndex].amount"
                            @change="updateAmounts(sku_item.originalIndex)"
                            :disabled="sku_item.is_out_of_stock"
                            :max="
                              sku_item.sku_count
                                ? parseInt(sku_item.sku_count)
                                : undefined
                            "
                          />
                          <button
                            class="qty-btn qty-btn-plus"
                            type="button"
                            @click="addAmounts(sku_item.originalIndex)"
                            :disabled="sku_item.is_out_of_stock"
                          >
                            <span class="qty-icon">+</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 打樣選項 -->
                <div class="sample-checkbox">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      class="checkbox-input"
                      v-model="ignoreBaseLine"
                      :true-value="false"
                      :false-value="true"
                      @change="ignoreBaseLineCheck(ignoreBaseLine)"
                      :disabled="displayViews.is_out_of_stock"
                    />
                    <span class="checkbox-box">
                      <i class="fa fa-check"></i>
                    </span>
                    <span class="checkbox-text"
                      >申請打樣 (允許購買單個商品)</span
                    >
                  </label>
                </div>

                <!-- 備註 -->
                <div class="notes-section">
                  <label class="notes-label">備註事項</label>
                  <textarea
                    class="notes-textarea"
                    rows="3"
                    placeholder="例如: 需要與廠商溝通降低起批數..."
                    v-model="prodDetail"
                  ></textarea>
                </div>

                <!-- 訂單摘要 -->
                <div v-if="orderSummary.totalCount > 0" class="order-summary">
                  <div class="summary-left">
                    <h4 class="summary-title">已選商品統計</h4>
                    <p class="summary-text">
                      共選取
                      <strong>{{ orderSummary.totalCount }}</strong> 件商品
                    </p>
                  </div>
                  <div class="summary-right">
                    <div class="summary-price">
                      ¥{{ orderSummary.totalPrice.toFixed(2) }}
                    </div>
                    <p class="summary-label">預估總金額</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 固定底部按鈕（手機版） -->
    <div v-if="displayViews != null" class="fixed-actions">
      <button
        class="action-button action-button-primary"
        @click="submit()"
        :disabled="displayViews.is_out_of_stock"
      >
        <i class="fa fa-cart-plus"></i>
        <span>加入購物車</span>
      </button>

      <button
        class="action-button action-button-secondary"
        @click="goToShoppingCart(addToCartCount)"
      >
        <span>前往購物車</span>
        <span v-if="addToCartCount > 0" class="action-badge">
          {{ addToCartCount }}
        </span>
      </button>
    </div>

    <loading
      loader="bars"
      :active.sync="isLoading"
      :can-cancel="true"
      :is-full-page="true"
    ></loading>
    <v-dialog />
  </div>
</template>

<style>
/* CSS 變數（必須在非 scoped 區塊） */
:root {
  --primary: #1a73e8;
  --primary-dark: #1557b0;
  --primary-light: #e8f0fe;
  --success: #34a853;
  --warning: #fbbc04;
  --danger: #ea4335;
  --gray-50: #f8f9fa;
  --gray-100: #f1f3f4;
  --gray-200: #e8eaed;
  --gray-300: #dadce0;
  --gray-600: #5f6368;
  --gray-900: #202124;
  --price-highlight: #d93025;
}
</style>

<style scoped>
/* 隱藏數字輸入框的箭頭 */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

/* 基礎佈局 */
.alibabaorder {
  min-height: 100vh;
  background-color: var(--gray-50);
  padding-top: 56px;
}

/* 固定頂部導航 */
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 56px;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: var(--primary);
  text-decoration: none;
}

.brand i {
  font-size: 20px;
}

.cart-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #ffffff;
  border: 2px solid var(--primary);
  border-radius: 24px;
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cart-button:hover {
  background: var(--primary-light);
}

.cart-button:active {
  transform: scale(0.98);
}

.cart-badge {
  background: var(--primary);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  min-width: 20px;
  text-align: center;
}

/* 內容容器 */
.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

/* 搜尋階段 */
.search-section {
  padding-top: 24px;
}

.search-container {
  max-width: 640px;
  margin: 0 auto;
}

.hero-text {
  text-align: center;
  margin-bottom: 32px;
}

.hero-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 12px;
}

.hero-subtitle {
  font-size: 16px;
  color: var(--gray-600);
  line-height: 1.5;
}

/* 搜尋卡片 */
.search-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.search-card-body {
  padding: 24px;
}

.input-label {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
}

.search-input-group {
  display: flex;
  gap: 0;
  margin-bottom: 8px;
}

.search-input {
  flex: 1;
  padding: 14px 16px;
  border: 2px solid var(--gray-300);
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: var(--primary);
  border-right: 2px solid var(--primary);
}

.search-input::placeholder {
  color: var(--gray-600);
}

.search-button {
  padding: 14px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 0 8px 8px 0;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.search-button:hover {
  background: var(--primary-dark);
}

.search-button:active {
  transform: scale(0.98);
}

.input-hint {
  font-size: 13px;
  color: var(--gray-600);
  margin: 0;
  padding-left: 4px;
}

.input-hint i {
  margin-right: 4px;
}

/* 歷史記錄 */
.history-section {
  margin-top: 20px;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-600);
  margin-bottom: 8px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-item:hover {
  background: white;
  border-color: var(--primary);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.history-text {
  flex: 1;
  font-size: 14px;
  color: var(--primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.history-delete:hover {
  background: var(--gray-200);
  color: var(--danger);
}

/* 資訊卡片 */
.info-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px;
  margin-bottom: 16px;
}

.info-card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 16px;
}

/* 網址範例 */
.url-examples {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-example {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.url-example-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--gray-600);
}

.url-example-label i {
  color: var(--primary);
}

.url-code {
  display: block;
  padding: 12px;
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 13px;
  color: #0066cc;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  word-break: break-all;
}

/* 保證卡片 */
.guarantee-card {
  border: 2px solid #d4edda;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
}

.guarantee-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, var(--success) 0%, #20c997 100%);
  border-radius: 8px;
  margin: -20px -20px 16px -20px;
}

.guarantee-header i {
  font-size: 24px;
  color: white;
  flex-shrink: 0;
}

.guarantee-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
}

.guarantee-header p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.guarantee-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.guarantee-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--gray-900);
}

.guarantee-list li i {
  color: var(--success);
  flex-shrink: 0;
  margin-top: 2px;
}

.guarantee-list li.tip {
  color: var(--gray-600);
}

.guarantee-list li.tip i {
  color: var(--warning);
}

.guarantee-list strong {
  color: var(--success);
}

.guarantee-list .highlight {
  color: var(--primary);
  font-weight: 600;
}

/* 商品區域 */
.product-section {
  padding-top: 16px;
}

/* 提示框 */
.alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
}

.alert i {
  flex-shrink: 0;
}

.alert-danger {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.alert-warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.alert-info {
  background: var(--primary-light);
  color: #1557b0;
  border: 1px solid #c2e0ff;
}

/* 商品佈局 */
.product-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

/* 商品主圖 */
.product-image-wrapper {
  width: 100%;
}

.product-image-sticky {
  position: sticky;
  top: 72px;
}

.product-image-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* 商品資訊卡片 */
.product-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-card-body {
  padding: 20px;
}

.product-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--gray-900);
  line-height: 1.4;
  margin-bottom: 12px;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.meta-item {
  font-size: 14px;
  color: var(--gray-600);
}

.meta-item strong {
  color: var(--gray-900);
  font-weight: 600;
}

/* 階梯定價 */
.tier-pricing {
  background: var(--primary-light);
  border: 1px solid #c2e0ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.tier-pricing-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
}

.tier-pricing-header i {
  color: var(--primary);
}

.tier-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border: 2px solid var(--gray-300);
  border-radius: 8px;
  font-size: 13px;
  color: var(--gray-900);
  transition: all 0.2s ease;
}

.tier-badge strong {
  color: var(--danger);
  margin-left: 4px;
}

.tier-badge-active {
  background: #d4edda;
  border-color: var(--success);
  color: #155724;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}

.tier-badge-active strong {
  color: #155724;
}

.tier-hint {
  font-size: 12px;
  color: var(--gray-600);
  margin: 0;
}

.tier-hint i {
  margin-right: 4px;
}

/* 定制商品標記 */
.custom-order-badge {
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}

/* 定制單價區塊 */
.custom-price-section {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.custom-price-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
}

.custom-price-header i {
  color: #e65100;
}

.custom-price-range {
  font-size: 13px;
  font-weight: 400;
  color: var(--gray-600);
}

.custom-price-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.custom-price-symbol {
  font-size: 20px;
  font-weight: 600;
  color: var(--danger);
}

.custom-price-input {
  width: 160px;
  padding: 8px 12px;
  border: 2px solid var(--gray-300);
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--danger);
  text-align: center;
  outline: none;
  transition: border-color 0.2s ease;
}

.custom-price-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.15);
}

.custom-price-input::placeholder {
  color: var(--gray-400);
  font-size: 14px;
  font-weight: 400;
}

.custom-price-unit {
  font-size: 14px;
  color: var(--gray-600);
}

.custom-price-hint {
  font-size: 12px;
  color: var(--gray-600);
  margin: 0;
}

.custom-price-hint i {
  margin-right: 4px;
}

/* 分隔線 */
.divider {
  height: 1px;
  background: var(--gray-200);
  margin: 24px 0;
}

/* 區塊標題 */
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
}

/* 顏色選擇 */
.color-selection {
  margin-bottom: 24px;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.color-item {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.color-item:hover:not(.color-item-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.color-image-wrapper {
  position: relative;
  padding-top: 100%;
  background: var(--gray-100);
  border: 2px solid var(--gray-300);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.color-item-selected .color-image-wrapper {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--primary-light);
}

.color-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.color-checkmark {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 24px;
  height: 24px;
  background: var(--primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.color-out-of-stock {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(52, 58, 64, 0.85);
  color: white;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-top-left-radius: 6px;
}

.color-item-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(100%);
}

.color-name {
  padding: 8px 4px;
  text-align: center;
  font-size: 13px;
  color: var(--gray-900);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* SKU 選擇 */
.sku-selection {
  margin-bottom: 24px;
}

/* SKU 卡片列表 */
.sku-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sku-card {
  background: white;
  border: 2px solid var(--gray-200);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;
}

.sku-card:hover:not(.sku-card-disabled) {
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(26, 115, 232, 0.12);
}

.sku-card-disabled {
  opacity: 0.5;
  background: var(--gray-50);
}

.sku-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gray-200);
}

.sku-card-title {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sku-name-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  line-height: 1.4;
}

.sku-out-badge {
  display: inline-flex;
  padding: 4px 10px;
  background: var(--danger);
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  width: fit-content;
}

.sku-price-display {
  font-size: 20px;
  font-weight: 700;
  color: var(--price-highlight);
  white-space: nowrap;
}

.sku-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.sku-stock-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--gray-100);
  border-radius: 8px;
}

.stock-label {
  font-size: 13px;
  color: var(--gray-600);
}

.stock-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray-900);
}

.sku-quantity-wrapper {
  display: flex;
  align-items: center;
  border: 2px solid var(--gray-300);
  border-radius: 10px;
  overflow: hidden;
}

.qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #ffffff;
  border: none;
  color: #1a73e8;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.qty-icon {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty-btn:active:not(:disabled) {
  background: #e8f0fe;
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-btn-minus {
  border-right: 1px solid #e8eaed;
  color: #1a73e8;
  background: #ffffff;
}

.qty-btn-plus {
  border-left: 1px solid #e8eaed;
  background: #1a73e8;
  color: #ffffff;
}

.qty-btn-plus:hover:not(:disabled) {
  background: #1557b0;
}

.qty-input {
  width: 56px;
  height: 44px;
  text-align: center;
  border: none;
  font-size: 16px;
  font-weight: 700;
  color: var(--gray-900);
}

.qty-input:disabled {
  opacity: 0.5;
}

/* 打樣選項 */
.sample-checkbox {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkbox-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid var(--gray-300);
  border-radius: 4px;
  background: white;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-box i {
  font-size: 12px;
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.checkbox-input:checked + .checkbox-box {
  background: var(--primary);
  border-color: var(--primary);
}

.checkbox-input:checked + .checkbox-box i {
  opacity: 1;
}

.checkbox-input:disabled + .checkbox-box {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkbox-text {
  font-size: 14px;
  color: var(--gray-900);
}

/* 備註 */
.notes-section {
  margin-bottom: 20px;
}

.notes-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-600);
  margin-bottom: 8px;
}

.notes-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--gray-300);
  border-radius: 8px;
  font-size: 14px;
  color: var(--gray-900);
  resize: vertical;
  outline: none;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.notes-textarea:focus {
  border-color: var(--primary);
}

.notes-textarea::placeholder {
  color: var(--gray-600);
}

/* 訂單摘要 */
.order-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: var(--primary-light);
  border: 1px solid #c2e0ff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.summary-left {
  flex: 1;
}

.summary-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 4px 0;
}

.summary-text {
  font-size: 13px;
  color: var(--gray-600);
  margin: 0;
}

.summary-text strong {
  color: var(--primary);
  font-weight: 700;
}

.summary-right {
  text-align: right;
}

.summary-price {
  font-size: 24px;
  font-weight: 700;
  color: var(--price-highlight);
  margin-bottom: 4px;
}

.summary-label {
  font-size: 12px;
  color: var(--gray-600);
  margin: 0;
}

/* 固定底部按鈕（手機版） */
.fixed-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: white;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 8px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
}

.action-button:active {
  transform: scale(0.98);
}

.action-button-primary {
  flex: 1;
  background: var(--danger);
  color: white;
}

.action-button-primary:hover {
  background: #c82333;
}

.action-button-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-button-secondary {
  flex: 1;
  background: white;
  color: var(--primary);
  border: 2px solid var(--primary);
}

.action-button-secondary:hover {
  background: var(--primary-light);
}

.action-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--primary);
  color: white;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
}

/* 響應式設計 */
@media (min-width: 576px) {
  .hero-title {
    font-size: 32px;
  }

  .search-card-body {
    padding: 32px;
  }

  .color-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 768px) {
  .content-wrapper {
    padding: 24px;
  }

  .search-section {
    padding-top: 40px;
  }

  .hero-title {
    font-size: 36px;
  }

  .color-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) {
  .header-container {
    padding: 0 24px;
  }

  .content-wrapper {
    padding: 32px 24px;
  }

  .search-section {
    padding-top: 56px;
  }

  .product-section {
    padding-top: 32px;
  }

  .product-layout {
    grid-template-columns: 5fr 7fr;
    gap: 24px;
  }

  .product-card-body {
    padding: 32px;
  }

  .color-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 12px;
  }

  /* 響應式：桌面版雙列 */
  .sku-card-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .sku-card:hover:not(.sku-card-disabled) {
    transform: translateY(-2px);
  }

  /* 桌面版顯示按鈕在卡片內 */
  .fixed-actions {
    position: static;
    background: transparent;
    padding: 0;
    box-shadow: none;
    display: flex;
    gap: 12px;
  }

  .action-button {
    flex: 1;
  }
}

/* 隱藏 Bootstrap 預設的 radio */
.d-none {
  display: none !important;
}

/* 手機版底部留空（防止被固定按鈕遮蓋） */
@media (max-width: 1023px) {
  .product-section {
    padding-bottom: 120px;
  }
}
</style>

<style>
/* Toast 通知居中（必須是全域樣式） */
.v-toast {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 9999 !important;
  width: auto !important;
  max-width: 90vw !important;
}

.v-toast__item {
  border-radius: 12px !important;
  padding: 16px 24px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25) !important;
  margin: 0 !important;
  animation: toast-pop-in 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55) !important;
}

.v-toast__item--success {
  background: #34a853 !important;
}

.v-toast__item--error {
  background: #ea4335 !important;
}

.v-toast__item--warning {
  background: #fbbc04 !important;
  color: #202124 !important;
}

.v-toast__item--info {
  background: #1a73e8 !important;
}

@keyframes toast-pop-in {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>

<script>
import VueLoading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import axios from 'axios'
import { isMobile } from 'mobile-device-detect'
export default {
  name: 'alibabaorder',
  components: {
    loading: VueLoading,
  },
  data: function () {
    return {
      apiUrl: process.env.VUE_APP_API_URL,
      displayViews: null,
      submitLimit: false,
      submitLimitCount: 20,
      skuInfo: [],
      buyBaseLine: 1,
      isLoading: true,
      searchUrl: '',
      mapInfo: null,
      prodInfos: {},
      ProdInfosArray: [],
      inputAmounts: [],
      colorSelected: null,
      selectedStore: 'UNIMARTC2C',
      checkMobile: isMobile ? '1' : '0',
      prodDetail: '',
      ignoreBaseLine: true,
      addToCartCount: 0,
      searchUrlHistory: [],
      customPrice: null,
    }
  },
  methods: {
    // Helper function to decode HTML entities
    decodeHTMLEntities: function (text) {
      if (!text || typeof text !== 'string') return text

      const entities = {
        '&gt;': '>',
        '&lt;': '<',
        '&amp;': '&',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'",
        '&nbsp;': ' ',
        '&#x3e;': '>',
        '&#x3c;': '<',
        '&#62;': '>',
        '&#60;': '<',
      }

      let decoded = text
      for (const [entity, char] of Object.entries(entities)) {
        decoded = decoded.split(entity).join(char)
      }
      return decoded
    },
    submit: function () {
      const vm = this

      // 檢查商品整體是否缺貨
      if (vm.displayViews.is_out_of_stock) {
        vm.$toast.open({
          message: '此商品已缺貨，無法加入購物車',
          type: 'error',
        })
        return 0
      }

      // 定制商品檢查單價
      if (vm.displayViews.is_custom_order) {
        if (!vm.customPrice || vm.customPrice <= 0) {
          vm.$toast.open({
            message: '請輸入定制單價',
            type: 'error',
          })
          return 0
        }
      }

      if (vm.displayViews.color_info.length > 0) {
        if (vm.colorSelected == null) {
          vm.$toast.open({
            message: '請選擇顏色!',
            type: 'error',
          })
          return 0
        }

        // 檢查選取的顏色是否缺貨
        const selectedColor = vm.displayViews.color_info[vm.colorSelected]
        if (selectedColor && selectedColor.is_out_of_stock) {
          vm.$toast.open({
            message: '您選擇的顏色已缺貨，請選擇其他顏色',
            type: 'error',
          })
          return 0
        }
      }

      if (vm.skuInfo.length > 0) {
        let count = 0
        vm.skuInfo.forEach(element => {
          count = parseInt(count) + parseInt(element.amount)
        })
        if (count == 0) {
          vm.$toast.open({
            message: '請輸入數量!',
            type: 'error',
          })
          return 0
        }
        //   } else if (count < vm.buyBaseLine && vm.ignoreBaseLine == "nonignore") {
        //     vm.$toast.open({
        //       message: "注意低於起批數量" + vm.buyBaseLine + "個",
        //       type: "error"
        //     });
        //     return 0;
        //   }
      }

      if (vm.displayViews != null) {
        vm.prodInfos['prod_name'] = vm.displayViews.prod_name
        vm.prodInfos['prod_name_china'] = vm.displayViews.prod_name_china
        vm.prodInfos['prod_vender'] = vm.displayViews.prod_vender
        vm.prodInfos['prod_location'] = vm.displayViews.prod_location
        vm.prodInfos['category'] = vm.displayViews.category
        vm.prodInfos['category_traditional'] =
          vm.displayViews.category_traditional
        vm.prodInfos['category_info'] = vm.displayViews.category_info
        vm.prodInfos['main_image_url'] = vm.displayViews.main_image_url
        vm.prodInfos['buy_base_line'] = vm.displayViews.buy_base_line
        vm.prodInfos['prod_url'] = vm.displayViews.prod_url
        // 儲存新版 API 欄位
        vm.prodInfos['origin_url'] = vm.displayViews.origin_url
        vm.prodInfos['start_amount'] = vm.displayViews.start_amount
        vm.prodInfos['total_can_book_count'] =
          vm.displayViews.total_can_book_count
        // 儲存階梯定價資訊
        vm.prodInfos['amount_info'] = vm.displayViews.amount_info || []
        vm.prodInfos['is_custom_order'] =
          vm.displayViews.is_custom_order || false
        try {
          vm.prodInfos['color_name_selected'] =
            vm.displayViews.color_info[vm.colorSelected].color_name
          vm.prodInfos['color_image_selected'] =
            vm.displayViews.color_info[vm.colorSelected].color_image
        } catch (e) {
          // console.error(e);
        }
        // 計算階梯價格
        let totalCount = 0
        vm.skuInfo.forEach(element => {
          totalCount += parseInt(element.amount) || 0
        })
        const tierPrice = vm.calculateTierPrice(
          totalCount,
          vm.displayViews.amount_info
        )

        // 複製並更新 SKU 價格
        let finalSkuInfo = JSON.parse(JSON.stringify(vm.skuInfo))
        if (vm.displayViews.is_custom_order && vm.customPrice > 0) {
          finalSkuInfo.forEach(sku => {
            sku.sku_price = vm.customPrice.toString()
          })
        } else if (tierPrice !== null) {
          finalSkuInfo.forEach(sku => {
            sku.sku_price = tierPrice.toString()
          })
        }

        vm.prodInfos['sku_info'] = finalSkuInfo
        vm.prodInfos['color_info'] = JSON.parse(
          JSON.stringify(vm.displayViews.color_info)
        )
        vm.prodInfos['prod_length'] = 0
        vm.prodInfos['prod_width'] = 0
        vm.prodInfos['prod_height'] = 0
        vm.prodInfos['prod_weight'] = 0
        vm.prodInfos['prod_detail'] = vm.prodDetail

        // Check if product already exists in cart
        const existingIndex = vm.ProdInfosArray.findIndex(
          item => item.prod_url === vm.prodInfos.prod_url
        )

        if (existingIndex !== -1) {
          // Product exists, merge data
          const existingItem = vm.ProdInfosArray[existingIndex]

          // 1. Merge SKU amounts
          vm.skuInfo.forEach(newSku => {
            if (newSku.amount > 0) {
              const existingSku = existingItem.sku_info.find(
                oldSku => oldSku.sku_id === newSku.sku_id
              )
              if (existingSku) {
                existingSku.amount =
                  (parseInt(existingSku.amount) || 0) + parseInt(newSku.amount)
              } else {
                // Should not happen if SKU structure is consistent, but safe to add if missing
                // However, structure usually matches. If strictly distinct, might need to push.
                // For simplicity and safety with current structure logic, we assume SKUs match.
              }
            }
          })

          // 2. Merge Color Names
          if (vm.prodInfos.color_name_selected) {
            const newColor = vm.prodInfos.color_name_selected
            const existingColors = existingItem.color_name_selected
              ? existingItem.color_name_selected.split(', ')
              : []

            if (!existingColors.includes(newColor)) {
              if (existingItem.color_name_selected) {
                existingItem.color_name_selected += `, ${newColor}`
              } else {
                existingItem.color_name_selected = newColor
              }
            }
          }

          // 3. Merge Details/Notes
          if (vm.prodDetail && vm.prodDetail.trim() !== '') {
            const newDetail = vm.prodDetail.trim()
            if (
              !existingItem.prod_detail ||
              !existingItem.prod_detail.includes(newDetail)
            ) {
              if (existingItem.prod_detail) {
                existingItem.prod_detail += ` | ${newDetail}`
              } else {
                existingItem.prod_detail = newDetail
              }
            }
          }

          // Update the array to trigger reactivity if needed (though object mutation works mostly)
          vm.$set(vm.ProdInfosArray, existingIndex, existingItem)
        } else {
          // New product, push to array
          vm.ProdInfosArray.push(vm.prodInfos)
        }

        vm.$store.prodInfos = vm.ProdInfosArray
        localStorage.setItem('prodInfos', JSON.stringify(vm.ProdInfosArray))
        vm.prodInfos = {}
        vm.skuInfo = vm.displayViews.sku_info

        let index = 0
        vm.displayViews.sku_info.forEach(function () {
          vm.skuInfo[index].amount = 0
          vm.skuInfo[index].prod_weight = 0
          vm.skuInfo[index].prod_length = 0
          vm.skuInfo[index].prod_width = 0
          vm.skuInfo[index].prod_height = 0
          index++
        })
        vm.prodDetail = ''

        vm.addToCartCount = vm.ProdInfosArray.length

        vm.$toast.open({
          message: vm.displayViews.prod_name + ' 加入購物車!',
          type: 'success',
        })
        vm.$forceUpdate()
        // vm.$router.push("profile");
      }
    },
    goToShoppingCart: function (prodCount) {
      const vm = this

      if (prodCount == 0) {
        vm.$toast.open({
          message: '請先將商品加入到購物車!',
          type: 'error',
        })
        return 0
      }
      // let count = 0;

      // vm.ProdInfosArray.forEach(function (prodInfo) {
      //   if (prodInfo.sku_info.length > 0) {
      //     prodInfo.sku_info.forEach(element => {
      //       count = parseInt(count) + parseInt(element.amount);
      //     });
      //   }
      // });

      // if (count == 0) {
      //   vm.$toast.open({
      //     message: "請輸入數量!",
      //     type: "error"
      //   });
      //   return 0;
      // } else if (count < vm.buyBaseLine && vm.ignoreBaseLine == "nonignore") {
      //   vm.$toast.open({
      //     message: "注意低於起批數量" +
      //       vm.buyBaseLine + "個",
      //     type: "error"
      //   });
      //   return 0;
      // } else {
      //   vm.$router.push("shoppingcart");
      // }

      vm.$router.replace('shoppingcartv1.1')
    },
    clickSearchUrlFormat: async function (url) {
      const vm = this
      // https: //detail.1688.com/offer/571008951577.html 或
      //   https: //m.1688.com/offer/571008951577.html 或
      // http://detail.m.1688.com/page/index.htm?offerId=601733052815
      // https://qr.1688.com/s/OyQiYCRy

      const QrAibabaRegex = /https:\/\/qr\.1688\.com\/s\/\w+/

      // 使用正則表達式進行匹配
      const matchQrUrl = url.match(QrAibabaRegex)
      if (matchQrUrl != undefined && matchQrUrl.length != 0) {
        try {
          const proxyUrl = `https://purchasebizapi.herokuapp.com/proxy/parse?url=${encodeURIComponent(
            matchQrUrl[0]
          )}`
          const response = await axios.get(proxyUrl)
          const finalUrl = response.data.trim() // 假設返回的 URL 在 response.data 中

          console.log('Final URL:', finalUrl)
          url = finalUrl // 更新 URL 為最終的重定向 URL
        } catch (error) {
          vm.$toast.open({
            message: '無法獲取重定向後的 URL: ' + error.message,
            type: 'error',
          })
          vm.isLoading = false
          return false
        }
      }

      let aibabaRegexs = []
      let isAibabaUrl = false
      let desktopAibabaRegex = /detail.1688.com\/offer\/[0-9]{8,15}/g
      let mobileAibabaRegex = /m.1688.com\/offer\/[0-9]{8,15}/g
      let mobileAppAibabaRegex =
        /detail.m.1688.com\/page\/index\.htm\?offerId=[0-9]{8,15}/g

      aibabaRegexs.push(desktopAibabaRegex)
      aibabaRegexs.push(mobileAibabaRegex)
      aibabaRegexs.push(mobileAppAibabaRegex)

      let checkMatchAibabaUrl = []
      let matchAibabaOfferId = ''
      aibabaRegexs.forEach(aibabaRegex => {
        checkMatchAibabaUrl = url.match(aibabaRegex)

        if (
          checkMatchAibabaUrl != undefined &&
          checkMatchAibabaUrl.length != 0
        ) {
          if (checkMatchAibabaUrl[0].split('offer/')[1] != undefined) {
            matchAibabaOfferId = checkMatchAibabaUrl[0].split('offer/')[1]
          } else if (checkMatchAibabaUrl[0].split('offerId=')[1] != undefined) {
            matchAibabaOfferId = checkMatchAibabaUrl[0].split('offerId=')[1]
          } else {
            isAibabaUrl = false
          }

          isAibabaUrl = true
        }
      })

      if (isAibabaUrl) {
        vm.searchUrl =
          'https://detail.1688.com/offer/' + matchAibabaOfferId + '.html'

        return true
      } else {
        vm.$toast.open({
          message: '輸入的 ' + vm.searchUrl + ' 網址格式錯誤',
          type: 'error',
        })
        vm.isLoading = false
        return false
      }
    },
    clickSearchUrl: async function () {
      const vm = this
      vm.isLoading = true

      if (vm.submitLimit == true) {
        vm.$toast.open({
          message:
            '🙋每日免費的申請代買委託單上限已經達到！請升級您的會員，將享受無上限申請代買委託單!',
          type: 'error',
        })
        vm.isLoading = false
        return 0
      }

      let urlIsValid = await vm.clickSearchUrlFormat(vm.searchUrl)

      if (urlIsValid) {
        vm.$toast.open({
          message: '解析網站中，大概需要15秒，請稍等片刻...',
          type: 'success',
        })

        let formData = {
          parseUrl: vm.searchUrl,
        }
        // NOTE: Use VUE_APP_USE_PROXY env to switch API
        // When USE_PROXY is "true" or not set, use ParseDataToDbByLocalServer
        // When USE_PROXY is "false", use ProxyGet1688
        const useProxy = process.env.VUE_APP_USE_PROXY
        const apiEndpoint =
          useProxy === 'false' ? 'ProxyGet1688' : 'ParseDataToDbByLocalServer'

        await axios
          .post(vm.apiUrl + apiEndpoint, formData, {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/json',
            },
          })
          .then(function (response) {
            const data = response.data

            // 檢查是否為有效的解析結果
            if (!data || data.error || !data.prod_name) {
              vm.$toast.open({
                message:
                  '解析失敗：' + ((data && data.message) || '無法取得商品資訊'),
                type: 'error',
              })
              vm.isLoading = false
              return
            }

            vm.displayViews = data
            // console.log(JSON.stringify(vm.displayViews));
            vm.buyBaseLine = vm.parseAlibabaBaseLine(vm.displayViews)
            vm.displayViews.prod_url = vm.searchUrl
            vm.displayViews.buy_base_line = vm.buyBaseLine

            // 定制商品初始化自訂單價
            if (data.is_custom_order) {
              vm.customPrice = parseFloat(data.cost_price_lowest) || null
            } else {
              vm.customPrice = null
            }

            localStorage.setItem(
              'recommendInfo',
              JSON.stringify(vm.displayViews.recommend_info)
            )

            vm.skuInfo = []
            vm.skuInfo = vm.displayViews.sku_info

            let index = 0
            vm.displayViews.sku_info.forEach(function (skuItem) {
              vm.$set(skuItem, 'amount', 0)
              vm.$set(skuItem, 'prod_weight', 0)
              vm.$set(skuItem, 'prod_length', 0)
              vm.$set(skuItem, 'prod_width', 0)
              vm.$set(skuItem, 'prod_height', 0)
              index++
            })

            //   console.log(response.data);
            vm.isLoading = false
            vm.getUserLevel()

            // 解析成功後儲存到歷史記錄
            vm.saveSearchUrlHistory(vm.searchUrl)

            vm.$toast.open({
              message: '解析成功',
              type: 'success',
            })
          })
          .catch(function (error) {
            // console.log(error);
            vm.$toast.open({
              message: '解析失敗: ' + error,
              type: 'error',
            })
            vm.$modal.show('dialog', {
              title: '🙋請聯絡客服',
              text: '請聯絡客服⚡ 告訴我們您網站解析發生了問題!',
              buttons: [
                {
                  title: '👉聯絡客服',
                  handler: () => {
                    window.location = 'https://m.me/2266831083395361'
                  },
                },
              ],
            })
            vm.isLoading = false
          })
      }
    },
    getUserLevel: function () {
      const vm = this
      let formData_getUserLevel = {
        userId: localStorage.userId,
      }
      console.log(formData_getUserLevel)
      axios
        .post(vm.apiUrl + 'GetUserLevel', formData_getUserLevel, {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
          },
        })
        .then(function (response) {
          // console.log(response.data);
          vm.isLoading = false
          if (response.data.level == 0) {
            vm.normalUserHistory()
          }
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    normalUserHistory: function () {
      const vm = this
      let formData_normalUserHistory = {
        userId: localStorage.userId,
      }
      axios
        .post(vm.apiUrl + 'Normal_user.history', formData_normalUserHistory, {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
          },
        })
        .then(function (response) {
          // console.log(response.data);
          if (response.data.submit_count >= vm.submitLimitCount) {
            vm.displayViews = null
            vm.submitLimit = true
            vm.$modal.show('dialog', {
              title:
                '🙋每日免費的申請代買委託單上限' +
                vm.submitLimitCount +
                '筆已經達到',
              text: '請升級您的會員，將享受無上限申請代買委託單!',
              buttons: [
                {
                  title: '👉立即升級30天黃金等級',
                  handler: () => {
                    window.location =
                      'https://ssl.smse.com.tw/ezpos/mtmk_utf.asp?Rvg2c=1&Dcvc=12872&Od_sob=123456&Amount=699&Remark=代買幫白金卡&Pay_zg=2&Roturl=https://purchasebizapi.herokuapp.com/SmilecallbackecpayLevelOrder&Pur_name=' +
                      localStorage.userId
                  },
                },
              ],
            })
          }
          vm.isLoading = false
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    parseAlibabaBaseLine: function (data) {
      let baseLine = 1

      // 優先使用新版 API 的 start_amount 欄位
      if (data.start_amount && data.start_amount > 0) {
        baseLine = data.start_amount
        return baseLine
      }

      // 向後兼容：使用舊版 amount_info 解析
      if (data.amount_info && data.amount_info.length >= 1) {
        let amountString = data.amount_info[0].amount_count

        if (typeof amountString === 'string') {
          if (amountString.startsWith('≥')) {
            baseLine = parseInt(amountString.split('≥')[1]) || 1
          } else if (amountString.indexOf('-') !== -1) {
            baseLine = parseInt(amountString.split('-')[0]) || 1
          } else {
            baseLine = parseInt(amountString) || 1
          }
        } else if (typeof amountString === 'number') {
          baseLine = amountString
        }
      }
      return baseLine
    },
    updateAmounts: function () {
      const vm = this
      // console.log(JSON.stringify(vm.skuInfo[index]));
      vm.$forceUpdate()
    },
    lessAmounts: function (index) {
      const vm = this

      let amount = Number(vm.skuInfo[index].amount) - 1
      if (!amount || amount <= 0) {
        amount = 0
      }

      vm.skuInfo[index].amount = amount

      // console.log(JSON.stringify(vm.skuInfo));
      vm.updateAmounts(index)
    },
    addAmounts: function (index) {
      const vm = this
      const skuItem = vm.displayViews.sku_info[index]

      // 如果商品缺貨,不允許增加數量
      if (skuItem.is_out_of_stock) {
        vm.$toast.open({
          message: '此規格已缺貨，無法購買',
          type: 'warning',
        })
        return
      }

      let amount = Number(vm.skuInfo[index].amount) + 1
      if (!amount) {
        amount = 1
      }

      // 檢查庫存上限
      if (skuItem.sku_count && amount > parseInt(skuItem.sku_count)) {
        vm.$toast.open({
          message: `此規格庫存僅剩 ${skuItem.sku_count} 件`,
          type: 'warning',
        })
        amount = parseInt(skuItem.sku_count)
      }

      vm.skuInfo[index].amount = amount

      // console.log(JSON.stringify(vm.skuInfo));
      vm.updateAmounts(index)
    },
    checkUnChatbotSignin: function () {
      const vm = this

      try {
        if (
          vm.$route.query.userId ||
          vm.$route.query.userImage ||
          vm.$route.query.userName
        ) {
          localStorage.userId = decodeURI(vm.$route.query.userId)
          localStorage.userImage = decodeURI(vm.$route.query.userImage)
          localStorage.userName = decodeURI(vm.$route.query.userName)
          vm.userId = decodeURI(vm.$route.query.userId)
          vm.userImage = decodeURI(vm.$route.query.userImage)
          vm.userName = decodeURI(vm.$route.query.userName)
        }
      } catch (e) {
        // console.info(e);
        alert(e)
      }
      console.log(vm.$route.query.userId, localStorage.userId, vm.userId)

      if (localStorage.userId == undefined) {
        if (vm.userId.length == 0) {
          vm.$modal.show('dialog', {
            title: '🙋您尚未登入FB會員',
            text: '請先登入才能使用此服務喔⚡',
            buttons: [
              {
                title: '👉用FB登入',
                handler: () => {
                  window.location = 'https://m.me/2266831083395361'
                },
              },
            ],
          })
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    },
    ignoreBaseLineCheck: function (ignoreBaseLine) {
      const vm = this
      vm.$store.state.ignoreBaseLine = ignoreBaseLine
    },
    // 載入搜尋網址歷史記錄
    loadSearchUrlHistory: function () {
      const vm = this
      try {
        const history = localStorage.getItem('searchUrlHistory')
        if (history) {
          const parsed = JSON.parse(history)
          // 遷移舊格式（純 URL 字串）為新格式（物件）
          vm.searchUrlHistory = parsed.map(item => {
            if (typeof item === 'string') {
              return { url: item, productName: null }
            }
            return item
          })
        }
      } catch (e) {
        console.error('載入歷史記錄失敗:', e)
        vm.searchUrlHistory = []
      }
    },
    // 儲存搜尋網址到歷史記錄（同時儲存商品名稱）
    saveSearchUrlHistory: function (url) {
      const vm = this
      if (!url || url.trim() === '') return

      // 建立歷史記錄物件，包含 URL 和商品名稱
      const historyItem = {
        url: url.trim(),
        productName:
          vm.displayViews && vm.displayViews.prod_name
            ? vm.displayViews.prod_name
            : null,
      }

      // 移除重複的 URL（如果已存在）
      vm.searchUrlHistory = vm.searchUrlHistory.filter(item => {
        const itemUrl = typeof item === 'string' ? item : item.url
        return itemUrl !== url.trim()
      })

      // 將新記錄加到陣列開頭
      vm.searchUrlHistory.unshift(historyItem)

      // 只保留最近 3 筆
      if (vm.searchUrlHistory.length > 3) {
        vm.searchUrlHistory = vm.searchUrlHistory.slice(0, 3)
      }

      // 儲存到 localStorage
      try {
        localStorage.setItem(
          'searchUrlHistory',
          JSON.stringify(vm.searchUrlHistory)
        )
      } catch (e) {
        console.error('儲存歷史記錄失敗:', e)
      }
    },
    // 刪除單筆歷史記錄
    removeSearchUrlHistory: function (index) {
      const vm = this
      vm.searchUrlHistory.splice(index, 1)
      try {
        localStorage.setItem(
          'searchUrlHistory',
          JSON.stringify(vm.searchUrlHistory)
        )
      } catch (e) {
        console.error('刪除歷史記錄失敗:', e)
      }
    },
    // 使用歷史記錄中的 URL
    useHistoryUrl: function (item) {
      const vm = this
      // 支援舊格式（字串）和新格式（物件）
      vm.searchUrl = typeof item === 'string' ? item : item.url
      // 可選：自動觸發搜尋
      // vm.clickSearchUrl()
    },
    // 取得歷史記錄項目的顯示文字（優先商品名稱，無則顯示網址）
    getHistoryItemDisplay: function (item) {
      // 支援舊格式（字串）和新格式（物件）
      if (typeof item === 'string') {
        return item
      }
      return item.productName || item.url
    },
    // 取得歷史記錄項目的網址（用於 title 屬性）
    getHistoryItemUrl: function (item) {
      return typeof item === 'string' ? item : item.url
    },
    // 根據購買數量計算階梯價格
    calculateTierPrice: function (totalQuantity, amountInfo) {
      const vm = this

      // 如果沒有階梯定價資訊，返回 null
      if (!amountInfo || amountInfo.length === 0) {
        return null
      }

      // 將階梯定價按數量從小到大排序
      const sortedTiers = [...amountInfo].sort(
        (a, b) => parseInt(a.amount_count) - parseInt(b.amount_count)
      )

      // 找出符合購買數量的最大階梯
      let applicableTier = sortedTiers[0] // 預設使用第一階（最低量）

      for (let tier of sortedTiers) {
        const tierCount = parseInt(tier.amount_count)
        if (totalQuantity >= tierCount) {
          applicableTier = tier
        } else {
          break
        }
      }

      return parseFloat(applicableTier.amount_price)
    },
  },
  watch: {},
  computed: {
    // 排序後的階梯定價資訊（從小到大）
    sortedAmountInfo: function () {
      const vm = this
      if (
        !vm.displayViews ||
        !vm.displayViews.amount_info ||
        vm.displayViews.amount_info.length === 0
      ) {
        return []
      }
      return [...vm.displayViews.amount_info].sort(
        (a, b) => parseInt(a.amount_count) - parseInt(b.amount_count)
      )
    },
    // 當前適用的階梯價格（根據購買總數量）
    currentTierPrice: function () {
      const vm = this
      if (
        !vm.displayViews ||
        !vm.displayViews.amount_info ||
        vm.displayViews.amount_info.length === 0
      ) {
        return null
      }
      // 獨立計算總數量，避免循環依賴
      let totalCount = 0
      vm.skuInfo.forEach(function (item) {
        totalCount += parseInt(item.amount) || 0
      })
      return vm.calculateTierPrice(totalCount, vm.displayViews.amount_info)
    },
    filteredSkuInfo: function () {
      const vm = this
      if (!vm.displayViews || !vm.displayViews.sku_info) return []

      // 為每個 SKU 加上原始索引
      const skusWithIndex = vm.displayViews.sku_info.map((item, index) => ({
        ...item,
        originalIndex: index,
      }))

      // 如果沒有顏色選項，顯示所有未缺貨的 SKU，並添加 displayName
      if (
        !vm.displayViews.color_info ||
        vm.displayViews.color_info.length === 0
      ) {
        return skusWithIndex
          .filter(sku => !sku.is_out_of_stock)
          .map(sku => ({
            ...sku,
            displayName: vm.decodeHTMLEntities(
              sku.sku_name_chinese || sku.sku_name || ''
            ),
          }))
      }

      // 如果有顏色選項但未選擇，不顯示任何 SKU (強制依序選擇)
      if (vm.colorSelected === null) {
        return []
      }

      // 取得選取顏色的中文名稱
      const selectedColor = vm.displayViews.color_info[vm.colorSelected]
      if (!selectedColor) return []

      const colorNameChinese = selectedColor.color_name_chinese

      // 過濾：名稱包含顏色名稱 且 未缺貨的 SKU
      // 修正：使用更精確的分段匹配，避免部分匹配 (如 "4S款" 匹配到 "雙料4S款")
      return skusWithIndex
        .filter(sku => {
          if (!sku.sku_name_chinese) return false

          // 先解碼 HTML 實體
          const decodedSkuName = vm.decodeHTMLEntities(sku.sku_name_chinese)

          // 1. 拆分規格字串 (使用解碼後的 ">" 分隔)
          const specs = decodedSkuName.split('>').map(s => s.trim())

          // 2. 檢查是否有任何一段完全等於選取的顏色名稱
          // 如果顏色名稱包含在規格中，通常會是其中一個獨立的段落
          const matchColor = specs.some(spec => spec === colorNameChinese)

          const isNotOutOfStock = !sku.is_out_of_stock

          // 如果分段精確匹配失敗，嘗試用 split(';') 或其他常見分隔符
          if (!matchColor && decodedSkuName.includes(';')) {
            const scSpecs = decodedSkuName.split(';').map(s => {
              // 處理 "顏色:紅色" 這種格式
              return s.includes(':') ? s.split(':')[1].trim() : s.trim()
            })
            if (scSpecs.some(spec => spec === colorNameChinese)) {
              return isNotOutOfStock
            }
          }

          // 最後的防線：如果上述都沒匹配到，但字串開頭或結尾剛好是顏色名，也算匹配
          // (例如 "4S款" 是整個字串，或者 "4S款 規格xxx")
          if (!matchColor) {
            // 再次確認是否真的是完全匹配 (極端情況)
            // 只有當 spec 完全等於 colorNameChinese 才是我們要的
            // 但為了向後兼容一些特殊格式，我們可以檢查 colorNameChinese 是否作為一個完整單詞出現
            // 這裡先維持 strict match，因為使用者明確指出 "雙料4S款" 是錯的
            return matchColor && isNotOutOfStock
          }

          return matchColor && isNotOutOfStock
        })
        .map(sku => {
          // 處理顯示名稱：移除顏色名稱前綴/後綴，並解碼 HTML 實體
          let displayName = vm.decodeHTMLEntities(sku.sku_name_chinese || '')
          if (displayName) {
            // 使用解碼後的 ">" 分隔符拆分
            const parts = displayName.split('>').map(s => s.trim())
            // 過濾掉等於顏色名稱的部分
            const nameParts = parts.filter(p => p !== colorNameChinese)

            if (nameParts.length > 0) {
              displayName = nameParts.join(' ')
            } else {
              // 如果過濾完什麼都不剩 (例如 sku_name 就是 color_name)，雖然少見但在只有一維規格時可能發生
              // 這種情況下，可能顯示 "預設" 或保留原名，或因為 table header 是 "規格"，顯示 "-"
              // 但為了安全起見，如果過濾後為空，還是顯示原名或 "標準規格"
              displayName = vm.decodeHTMLEntities(sku.sku_name) || displayName
            }
          }
          return {
            ...sku,
            displayName,
          }
        })
    },
    orderSummary: function () {
      const vm = this
      let totalCount = 0
      let totalPrice = 0

      // 先計算總數量
      vm.skuInfo.forEach(item => {
        const amount = parseInt(item.amount) || 0
        totalCount += amount
      })

      // 根據總數量決定使用階梯價格還是 SKU 價格
      const amountInfo = vm.displayViews && vm.displayViews.amount_info
      const tierPrice = vm.calculateTierPrice(totalCount, amountInfo)

      // 定制商品使用自訂單價
      const isCustom = vm.displayViews && vm.displayViews.is_custom_order

      vm.skuInfo.forEach(item => {
        const amount = parseInt(item.amount) || 0
        if (amount > 0) {
          let price = 0
          if (isCustom && vm.customPrice > 0) {
            price = vm.customPrice
          } else if (tierPrice !== null) {
            price = tierPrice
          } else {
            price = parseFloat(item.sku_price) || 0
          }
          totalPrice += amount * price
        }
      })

      return {
        totalCount,
        totalPrice,
        tierPrice, // 返回當前適用的階梯價格（用於顯示）
      }
    },
  },
  mounted() {
    const vm = this
    vm.isLoading = false
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    // 載入搜尋網址歷史記錄
    vm.loadSearchUrlHistory()

    // Event snippet for 進入機器人解析阿里巴巴網站page conversion page
    vm.$ga.event('event', 'conversion', {
      send_to: 'AW-700538278/FijJCLLZ3LIBEKa7hc4C',
      value: 1.0,
      currency: 'TWD',
    })

    vm.checkUnChatbotSignin()

    if (vm.$store.prodInfos != undefined && vm.$store.prodInfos.length > 0) {
      vm.ProdInfosArray = vm.$store.prodInfos.concat()
      vm.displayViews = JSON.parse(JSON.stringify(vm.ProdInfosArray[0]))

      vm.skuInfo = vm.displayViews.sku_info
      let index = 0
      vm.displayViews.sku_info.forEach(function (skuItem) {
        vm.$set(skuItem, 'amount', 0)
        vm.$set(skuItem, 'prod_weight', 0)
        vm.$set(skuItem, 'prod_length', 0)
        vm.$set(skuItem, 'prod_width', 0)
        vm.$set(skuItem, 'prod_height', 0)
        index++
      })
      vm.prodDetail = ''
      vm.addToCartCount = vm.ProdInfosArray.length

      vm.isLoading = false
    } else {
      const storedProdInfos = localStorage.getItem('prodInfos')
      if (storedProdInfos && storedProdInfos !== 'undefined') {
        try {
          vm.ProdInfosArray = JSON.parse(storedProdInfos).concat()
        } catch (e) {
          console.error('Failed to parse prodInfos:', e)
          localStorage.removeItem('prodInfos')
          return
        }
      } else {
        return
      }
      if (vm.ProdInfosArray.length === 0) {
        return
      }
      vm.displayViews = JSON.parse(JSON.stringify(vm.ProdInfosArray[0]))

      // console.log(vm.displayViews);
      vm.skuInfo = vm.displayViews.sku_info
      let index = 0
      vm.displayViews.sku_info.forEach(function (skuItem) {
        vm.$set(skuItem, 'amount', 0)
        vm.$set(skuItem, 'prod_weight', 0)
        vm.$set(skuItem, 'prod_length', 0)
        vm.$set(skuItem, 'prod_width', 0)
        vm.$set(skuItem, 'prod_height', 0)
        index++
      })
      vm.prodDetail = ''
      vm.addToCartCount = vm.ProdInfosArray.length

      vm.isLoading = false
    }
  },
}
</script>
